import { useState } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";

import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ScrollFadeWrapper from "@/components/ui/scroll-fade-wrapper";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import VoidInvoiceConfirmationModal from "@/features/invoices/components/invoice/VoidInvoiceConfirmationModal";
import RecurringIcon from "@/features/invoices/components/recurring/RecurringIcon";
import { Invoice } from "@/features/invoices/types/invoices";
import { track } from "@/utils/mixpanel";

import { CircleClose, Copy, DocDownload } from "../../../components/icons";

interface InvoiceTableProps {
  invoices: Invoice[];
  isLoading: boolean;
  onDownloadInvoice: (invoiceId: string) => void;
  onRowClick?: (invoice: Invoice) => void;
  onRecurringIconClick?: (recurringInvoiceId: string) => void;
}
const InvoiceTable = ({
  invoices,
  isLoading,
  onDownloadInvoice,
  onRowClick,
  onRecurringIconClick,
}: InvoiceTableProps) => {
  const [voidConfirmationOpen, setVoidConfirmationOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };
  const handleCopyLink = async (invoice: Invoice) => {
    if (!invoice.hostedInvoiceUrl) return;
    try {
      await navigator.clipboard.writeText(invoice.hostedInvoiceUrl);
      toast.success("Invoice link copied to clipboard");
      track("copy_invoice_link", {
        invoice_id: invoice.id,
        invoice_number: invoice.number,
        status: invoice.status,
      });
    } catch (error) {
      toast.error("Failed to copy invoice link");
    }
  };
  const handleVoidInvoice = (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedInvoice(invoice);
    setVoidConfirmationOpen(true);
  };
  const handleRowClick = (invoice: Invoice, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button") === null && onRowClick) {
      onRowClick(invoice);
      track("clicked_invoice", {
        invoice_id: invoice.id,
        invoice_number: invoice.number,
        status: invoice.status,
        amount: invoice.amount,
        currency: invoice.currency,
      });
    }
  };

  // Check if an invoice can be voided (only open status)
  const canVoidInvoice = (invoice: Invoice) => {
    return invoice.status === "open";
  };
  return (
    <>
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="flex items-center gap-4">
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-start py-16 px-3">
          <FileText className="h-5 w-5 text-[#767677]" />
          <div className="mt-2 text-[#767677] text-sm font-semibold">
            No invoices yet
          </div>
        </div>
      ) : (
        <ScrollFadeWrapper>
          <Table bordered={false} className="min-w-[800px]">
            <TableHeader className="bg-transparent">
              <TableRow>
                <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] whitespace-nowrap">
                  Invoice #
                </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] whitespace-nowrap">
                Created
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] whitespace-nowrap">
                Customer
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] whitespace-nowrap">
                Project
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] whitespace-nowrap">
                Amount
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] whitespace-nowrap">
                Due date
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] whitespace-nowrap">
                Status
              </TableHead>
              <TableHead className="px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677] text-right whitespace-nowrap">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow
                key={invoice.id}
                onClick={(e) => handleRowClick(invoice, e)}
                className={`${
                  onRowClick
                    ? "cursor-pointer hover:bg-sds-neutral-00 transition-colors"
                    : ""
                } text-[12px] font-semibold`}
              >
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                  {invoice.number}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                  {formatDate(invoice.created)}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                  <div className="flex items-center gap-1.5">
                    {invoice.customer || "-"}
                    {invoice.isRecurring && (
                      <RecurringIcon
                        size="sm"
                        tooltipText="Part of a recurring invoice"
                        recurringInvoiceId={invoice.subscriptionId || undefined}
                        onClick={
                          invoice.subscriptionId && onRecurringIconClick
                            ? (recurringId) => {
                                track("recurring_icon_clicked", {
                                  invoice_id: invoice.id,
                                  recurring_invoice_id: recurringId,
                                });
                                onRecurringIconClick(recurringId);
                              }
                            : undefined
                        }
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold max-w-[120px] truncate">
                  {invoice.projectName || "-"}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                  {formatCurrency(invoice.amount, invoice.currency)}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                  {invoice.dueDate ? formatDate(invoice.dueDate) : "-"}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] font-semibold">
                  {invoice.status === "paid" ? (
                    <Badge
                      variant="opaque"
                      color="green"
                      className="px-[6px] py-[2px] text-[12px] font-semibold"
                    >
                      Paid
                    </Badge>
                  ) : invoice.status === "void" ? (
                    <Badge
                      variant="opaque"
                      color="red"
                      className="px-[6px] py-[2px] text-[12px] font-semibold"
                    >
                      Void
                    </Badge>
                  ) : invoice.status === "draft" ? (
                    <Badge
                      variant="opaque"
                      color="grey"
                      className="px-[6px] py-[2px] text-[12px] font-semibold"
                    >
                      Draft
                    </Badge>
                  ) : (
                    <Badge
                      variant="opaque"
                      color="yellow"
                      className="px-[6px] py-[2px] text-[12px] font-semibold capitalize"
                    >
                      {invoice.status}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="px-3 py-1.5 text-right">
                  <div className="inline-flex items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyLink(invoice)}
                      className="h-8 w-8 p-0"
                      disabled={!invoice.hostedInvoiceUrl}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDownloadInvoice(invoice.id)}
                      className="h-8 w-8 p-0"
                      disabled={!invoice.pdfUrl}
                    >
                      <DocDownload className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleVoidInvoice(invoice, e)}
                      className={`h-8 w-8 p-0 ${
                        canVoidInvoice(invoice)
                          ? "text-red-600 hover:text-red-700 hover:bg-red-50"
                          : "text-gray-400"
                      }`}
                      disabled={!canVoidInvoice(invoice)}
                    >
                      <CircleClose className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </ScrollFadeWrapper>
      )}

      {selectedInvoice && (
        <VoidInvoiceConfirmationModal
          open={voidConfirmationOpen}
          onOpenChange={setVoidConfirmationOpen}
          invoiceId={selectedInvoice.id}
          invoiceNumber={selectedInvoice.number}
        />
      )}
    </>
  );
};
export default InvoiceTable;
